/**
 * TDBStockRegister — Roji's monthly opening/purchases/closing register.
 *
 * Phone-friendly: cards collapse on mobile, side-by-side on desktop.
 * 3 city columns × N items per city · 2 sections (Raw / Packaging).
 *
 * Workflow:
 *   1. Open the month picker → loads `GET /api/tdb-mis/stock/register?month_ym=`
 *   2. "Carry forward" pulls prev month's signed closings into opening qty
 *   3. Edit cells inline (opening · purchases · closing · rate)
 *   4. Consumption + cost recompute automatically server-side
 *   5. Roji clicks "Sign off" per row → row locks
 *
 * Only Roji (or CEO) can sign off. Once signed, row is read-only.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Boxes, RefreshCw, CheckCircle2, Lock, AlertTriangle, ChevronRight, Plus, Sparkles,
} from 'lucide-react';
import api from '@/lib/api';
import { OtherRowDraft, PendingPromotionsBadge, PLACEHOLDER_NAMES } from '@/components/TDBStockOtherFeature';

const CITIES = ['Bengaluru', 'Mumbai', 'Gurgaon'];

function todayIST() {
  const ist = new Date(Date.now() + 5.5 * 3600 * 1000);
  return ist.toISOString().slice(0, 10);
}

function thisMonthYM() {
  return todayIST().slice(0, 7);
}

function fmtQty(q, unit) {
  if (q === null || q === undefined) return '—';
  return `${Number(q).toLocaleString('en-IN', { maximumFractionDigits: 2 })} ${unit || ''}`.trim();
}

function fmtRupees(n) {
  if (!n) return '₹0';
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

export default function TDBStockRegister({ className = '' }) {
  const [monthYM, setMonthYM] = useState(thisMonthYM());
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({}); // key = month|city|item
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [err, setErr] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const [ri, rr] = await Promise.all([
        api.get('/tdb-mis/stock/items'),
        api.get('/tdb-mis/stock/register', { params: { month_ym: monthYM } }),
      ]);
      setItems(ri.data.items || []);
      setRows(rr.data.rows || []);
      const next = {};
      for (const r of rr.data.rows || []) {
        // Composite key includes custom_name for 'Other —' rows
        const cn = r.custom_name_norm || '';
        const k = cn
          ? `${r.month_ym}|${r.city}|${r.item}|${cn}`
          : `${r.month_ym}|${r.city}|${r.item}`;
        next[k] = {
          opening_qty: String(r.opening_qty ?? ''),
          purchases_qty: String(r.purchases_qty ?? ''),
          closing_qty: r.closing_qty === null || r.closing_qty === undefined ? '' : String(r.closing_qty),
          rate: String(r.rate ?? ''),
        };
      }
      setDrafts(next);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load stock register');
    } finally {
      setLoading(false);
    }
  }, [monthYM]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const carryForward = useCallback(async () => {
    setBusyKey('carry');
    try {
      const r = await api.post('/tdb-mis/stock/register/carry-forward', { month_ym: monthYM });
      await loadAll();
      alert(`Carried forward ${r.data.inserted} row(s) from previous month.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Carry-forward failed');
    } finally {
      setBusyKey('');
    }
  }, [monthYM, loadAll]);

  const saveRow = useCallback(async (city, item) => {
    const key = `${monthYM}|${city}|${item}`;
    const d = drafts[key] || {};
    setBusyKey(key);
    setErr('');
    try {
      await api.put('/tdb-mis/stock/register', {
        month_ym: monthYM, city, item,
        opening_qty: parseFloat(d.opening_qty) || 0,
        purchases_qty: parseFloat(d.purchases_qty) || 0,
        closing_qty: d.closing_qty === '' ? null : parseFloat(d.closing_qty),
        rate: parseFloat(d.rate) || 0,
      });
      await loadAll();
    } catch (e) {
      setErr(e?.response?.data?.detail || `Save failed for ${city}/${item}`);
    } finally {
      setBusyKey('');
    }
  }, [drafts, monthYM, loadAll]);

  const signRow = useCallback(async (city, item) => {
    if (!window.confirm(`Sign off ${city} / ${item}? Row will lock from edits.`)) return;
    setBusyKey(`sign|${city}|${item}`);
    try {
      await api.post('/tdb-mis/stock/register/sign', { month_ym: monthYM, city, item });
      await loadAll();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Sign-off failed');
    } finally {
      setBusyKey('');
    }
  }, [monthYM, loadAll]);

  // Group rows by (city, item) for the master grid · Other rows live separately
  const rowsByCityItem = useMemo(() => {
    const m = {};
    for (const r of rows) {
      if (PLACEHOLDER_NAMES.includes(r.item)) continue;  // skip Other in main grid
      m[`${r.city}|${r.item}`] = r;
    }
    return m;
  }, [rows]);

  // 'Other —' rows · grouped by (city, category) for the bottom-of-section list
  const otherRowsByCityCategory = useMemo(() => {
    const m = {};
    const placeholderToCat = {
      'Other — Raw Material': 'raw',
      'Other — Packaging': 'packaging',
      'Other — Consumable': 'consumable',
    };
    for (const r of rows) {
      const cat = placeholderToCat[r.item];
      if (!cat) continue;
      const key = `${r.city}|${cat}`;
      (m[key] = m[key] || []).push(r);
    }
    return m;
  }, [rows]);

  const itemsByCategory = useMemo(() => {
    const out = { raw: [], packaging: [], consumable: [] };
    for (const it of items) {
      if (it.is_other_placeholder) continue;  // hide from main grid
      (out[it.category] || (out[it.category] = [])).push(it);
    }
    return out;
  }, [items]);

  const stats = useMemo(() => {
    let total = 0;
    let signed = 0;
    for (const city of CITIES) {
      for (const it of items) {
        if (it.is_other_placeholder) continue;
        total += 1;
        const r = rowsByCityItem[`${city}|${it.item_name}`];
        if (r?.signed_off_at) signed += 1;
      }
    }
    return { total, signed, pct: total > 0 ? Math.round(signed / total * 100) : 0 };
  }, [items, rowsByCityItem]);

  // ─── Save / sign for one row, handling Other-rows' composite key ───
  const saveExistingOther = useCallback(async (r) => {
    const cn = r.custom_name_norm || '';
    const key = `${monthYM}|${r.city}|${r.item}|${cn}`;
    const d = drafts[key] || {};
    setBusyKey(key);
    setErr('');
    try {
      await api.put('/tdb-mis/stock/register', {
        month_ym: monthYM,
        city: r.city,
        item: r.item,
        custom_name: r.custom_name,
        opening_qty: parseFloat(d.opening_qty) || 0,
        purchases_qty: parseFloat(d.purchases_qty) || 0,
        closing_qty: d.closing_qty === '' ? null : parseFloat(d.closing_qty),
        rate: parseFloat(d.rate) || 0,
      });
      await loadAll();
    } catch (e) {
      setErr(e?.response?.data?.detail || `Save failed for ${r.city}/${r.item}/${r.custom_name}`);
    } finally {
      setBusyKey('');
    }
  }, [drafts, monthYM, loadAll]);

  const signExistingOther = useCallback(async (r) => {
    if (!window.confirm(`Sign off ${r.city} / ${r.item} · ${r.custom_name}? Row will lock from edits.`)) return;
    setBusyKey(`sign|${r.city}|${r.item}|${r.custom_name_norm}`);
    try {
      await api.post('/tdb-mis/stock/register/sign', {
        month_ym: monthYM,
        city: r.city,
        item: r.item,
        custom_name: r.custom_name,
      });
      await loadAll();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Sign-off failed');
    } finally {
      setBusyKey('');
    }
  }, [monthYM, loadAll]);

  // City × category keys for the "+ Add Other" draft toggle
  const [activeDrafts, setActiveDrafts] = useState({});  // key = city|category → bool

  return (
    <div
      data-testid="tdb-stock-register"
      className={`bg-white rounded-2xl border border-gray-200 p-6 ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Boxes className="w-5 h-5 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">Stock Register · Roji</h3>
          <span className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
            {stats.signed}/{stats.total} signed · {stats.pct}%
          </span>
          <PendingPromotionsBadge onAction={loadAll} />
        </div>
        <div className="flex items-center gap-2">
          <input
            data-testid="tdb-stock-register-month"
            type="month"
            value={monthYM}
            onChange={(e) => setMonthYM(e.target.value)}
            max={thisMonthYM()}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white"
          />
          <button
            data-testid="tdb-stock-register-carry-forward"
            onClick={carryForward}
            disabled={busyKey === 'carry'}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 flex items-center gap-1"
            title="Bring forward last month's signed closings"
          >
            <ChevronRight className="w-3 h-3" /> Carry-forward
          </button>
          <button
            onClick={loadAll}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50"
            title="Reload"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {err && (
        <div data-testid="tdb-stock-register-error" className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {err}
        </div>
      )}

      {['raw', 'packaging', 'consumable'].map((cat) => {
        const catLabels = {
          raw: '🍞 Raw materials',
          packaging: '📦 Packaging',
          consumable: '🧹 Consumables (operating cost · not food)',
        };
        return (
        <div key={cat} className="mb-4" data-testid={`tdb-stock-register-section-${cat}`}>
          <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1.5">
            {catLabels[cat]}
          </h4>
          <div className="space-y-1.5">
            {(itemsByCategory[cat] || []).map((it) => (
              <div key={it.item_name} className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {CITIES.map((city) => {
                  const key = `${monthYM}|${city}|${it.item_name}`;
                  const r = rowsByCityItem[`${city}|${it.item_name}`];
                  const signed = !!r?.signed_off_at;
                  const d = drafts[key] || {};
                  return (
                    <div
                      key={city}
                      data-testid={`tdb-stock-cell-${cat}-${city.toLowerCase()}-${it.item_name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`}
                      className={`p-2 rounded-md border text-xs ${signed ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-200'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-800 truncate">{it.item_name} · {city}</span>
                        {signed && (
                          <span className="text-emerald-700 flex items-center gap-0.5"><Lock className="w-3 h-3" />Signed</span>
                        )}
                      </div>
                      <div className="grid grid-cols-4 gap-1">
                        <input
                          type="number"
                          step="0.001"
                          placeholder="Opening"
                          value={d.opening_qty ?? ''}
                          onChange={(e) => setDrafts({ ...drafts, [key]: { ...d, opening_qty: e.target.value }})}
                          onBlur={() => saveRow(city, it.item_name)}
                          disabled={signed}
                          className="text-right text-[11px] px-1 py-0.5 rounded border border-gray-200 disabled:bg-emerald-50/50"
                        />
                        <input
                          type="number"
                          step="0.001"
                          placeholder="Purch."
                          value={d.purchases_qty ?? ''}
                          onChange={(e) => setDrafts({ ...drafts, [key]: { ...d, purchases_qty: e.target.value }})}
                          onBlur={() => saveRow(city, it.item_name)}
                          disabled={signed}
                          className="text-right text-[11px] px-1 py-0.5 rounded border border-gray-200 disabled:bg-emerald-50/50"
                        />
                        <input
                          type="number"
                          step="0.001"
                          placeholder="Closing"
                          value={d.closing_qty ?? ''}
                          onChange={(e) => setDrafts({ ...drafts, [key]: { ...d, closing_qty: e.target.value }})}
                          onBlur={() => saveRow(city, it.item_name)}
                          disabled={signed}
                          className="text-right text-[11px] px-1 py-0.5 rounded border border-gray-200 disabled:bg-emerald-50/50"
                        />
                        <input
                          type="number"
                          step="0.01"
                          placeholder="₹ rate"
                          value={d.rate ?? ''}
                          onChange={(e) => setDrafts({ ...drafts, [key]: { ...d, rate: e.target.value }})}
                          onBlur={() => saveRow(city, it.item_name)}
                          disabled={signed}
                          className="text-right text-[11px] px-1 py-0.5 rounded border border-gray-200 disabled:bg-emerald-50/50"
                        />
                      </div>
                      <div className="flex items-center justify-between mt-1 text-[10px] text-gray-600">
                        <span>
                          Cons: <strong>{fmtQty(r?.consumption_qty, it.unit)}</strong> · {fmtRupees(r?.consumption_cost)}
                        </span>
                        {!signed && r && r.closing_qty !== null && (
                          <button
                            data-testid={`tdb-stock-sign-${cat}-${city.toLowerCase()}-${it.item_name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`}
                            onClick={() => signRow(city, it.item_name)}
                            className="text-emerald-700 hover:text-emerald-900 font-medium flex items-center gap-0.5"
                          >
                            <CheckCircle2 className="w-3 h-3" /> Sign
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* "Other —" rows · existing entries first, then "+ Add Other" CTA */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2 pt-2 border-t border-dashed border-amber-200">
              {CITIES.map((city) => {
                const existingOthers = otherRowsByCityCategory[`${city}|${cat}`] || [];
                const draftKey = `${city}|${cat}`;
                const showDraft = !!activeDrafts[draftKey];
                return (
                  <div key={city} className="space-y-1.5">
                    {existingOthers.map((r) => {
                      const cn = r.custom_name_norm || '';
                      const key = `${monthYM}|${r.city}|${r.item}|${cn}`;
                      const d = drafts[key] || {};
                      const signed = !!r.signed_off_at;
                      return (
                        <div
                          key={key}
                          data-testid={`tdb-stock-other-row-${cat}-${city.toLowerCase()}-${cn.replace(/[^a-z0-9]/gi, '_')}`}
                          className={`p-2 rounded-md border text-xs ${signed ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50/40 border-amber-200'}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-amber-900 truncate flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              Other · {r.custom_name} · {city}
                            </span>
                            {signed && (
                              <span className="text-emerald-700 flex items-center gap-0.5"><Lock className="w-3 h-3" />Signed</span>
                            )}
                          </div>
                          <div className="grid grid-cols-4 gap-1">
                            <input
                              type="number" step="0.001" placeholder="Opening"
                              value={d.opening_qty ?? ''}
                              onChange={(e) => setDrafts({ ...drafts, [key]: { ...d, opening_qty: e.target.value }})}
                              onBlur={() => saveExistingOther(r)}
                              disabled={signed}
                              className="text-right text-[11px] px-1 py-0.5 rounded border border-amber-200 disabled:bg-emerald-50/50"
                            />
                            <input
                              type="number" step="0.001" placeholder="Purch."
                              value={d.purchases_qty ?? ''}
                              onChange={(e) => setDrafts({ ...drafts, [key]: { ...d, purchases_qty: e.target.value }})}
                              onBlur={() => saveExistingOther(r)}
                              disabled={signed}
                              className="text-right text-[11px] px-1 py-0.5 rounded border border-amber-200 disabled:bg-emerald-50/50"
                            />
                            <input
                              type="number" step="0.001" placeholder="Closing"
                              value={d.closing_qty ?? ''}
                              onChange={(e) => setDrafts({ ...drafts, [key]: { ...d, closing_qty: e.target.value }})}
                              onBlur={() => saveExistingOther(r)}
                              disabled={signed}
                              className="text-right text-[11px] px-1 py-0.5 rounded border border-amber-200 disabled:bg-emerald-50/50"
                            />
                            <input
                              type="number" step="0.01" placeholder="₹ rate"
                              value={d.rate ?? ''}
                              onChange={(e) => setDrafts({ ...drafts, [key]: { ...d, rate: e.target.value }})}
                              onBlur={() => saveExistingOther(r)}
                              disabled={signed}
                              className="text-right text-[11px] px-1 py-0.5 rounded border border-amber-200 disabled:bg-emerald-50/50"
                            />
                          </div>
                          <div className="flex items-center justify-between mt-1 text-[10px] text-amber-900/80">
                            <span>
                              Cons: <strong>{fmtQty(r.consumption_qty, '')}</strong> · {fmtRupees(r.consumption_cost)}
                            </span>
                            {!signed && r.closing_qty !== null && (
                              <button
                                data-testid={`tdb-stock-sign-other-${cat}-${city.toLowerCase()}-${cn.replace(/[^a-z0-9]/gi, '_')}`}
                                onClick={() => signExistingOther(r)}
                                className="text-emerald-700 hover:text-emerald-900 font-medium flex items-center gap-0.5"
                              >
                                <CheckCircle2 className="w-3 h-3" /> Sign
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {showDraft ? (
                      <OtherRowDraft
                        monthYM={monthYM}
                        city={city}
                        category={cat}
                        onSaved={() => {
                          setActiveDrafts({ ...activeDrafts, [draftKey]: false });
                          loadAll();
                        }}
                        onCancel={() => setActiveDrafts({ ...activeDrafts, [draftKey]: false })}
                      />
                    ) : (
                      <button
                        type="button"
                        data-testid={`tdb-stock-add-other-${cat}-${city.toLowerCase()}`}
                        onClick={() => setActiveDrafts({ ...activeDrafts, [draftKey]: true })}
                        className="w-full text-[11px] font-medium px-2 py-1.5 rounded-md border border-dashed border-amber-400 text-amber-800 hover:bg-amber-50 flex items-center justify-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Add Other · {city}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        );
      })}

      <div className="mt-2 text-[10px] text-gray-500">
        Consumption = opening + purchases − closing · saves on blur · only Roji or CEO can sign-off
      </div>
    </div>
  );
}
